import { useEffect, useRef } from 'react'

export default function Brand3DCanvas({ className = '', style = {} }) {
  const canvasRef = useRef(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let animationFrameId
    let width = (canvas.width = canvas.parentElement?.clientWidth || 360)
    let height = (canvas.height = canvas.parentElement?.clientHeight || 200)

    let mouseX = 0
    let mouseY = 0
    let targetRotX = 0
    let targetRotY = 0
    let rotX = 0
    let rotY = 0
    let scrollOffset = 0

    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect()
      const x = (e.clientX - rect.left) / rect.width - 0.5
      const y = (e.clientY - rect.top) / rect.height - 0.5
      mouseX = x
      mouseY = y
      targetRotY = mouseX * 0.6
      targetRotX = -mouseY * 0.4
    }

    const handleScroll = () => {
      scrollOffset = window.scrollY * 0.002
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('scroll', handleScroll, { passive: true })

    const handleResize = () => {
      if (!canvas.parentElement) return
      width = canvas.width = canvas.parentElement.clientWidth
      height = canvas.height = canvas.parentElement.clientHeight
    }
    window.addEventListener('resize', handleResize)

    // 3D Data Bars Definition (Logo inspired: Coral, Teal, Yellow)
    const bars = [
      { x: -55, baseHeight: 45, width: 26, depth: 26, colorFront: '#E25B3C', colorTop: '#F07559', colorSide: '#C84628' },
      { x: -15, baseHeight: 75, width: 26, depth: 26, colorFront: '#2F9EBB', colorTop: '#4EBDDA', colorSide: '#207F99' },
      { x: 25, baseHeight: 110, width: 26, depth: 26, colorFront: '#F3D03E', colorTop: '#FFE46B', colorSide: '#D4B022' }
    ]

    // Sparkling Ruby / Wine Star Particles
    const stars = Array.from({ length: 12 }, () => ({
      x: (Math.random() - 0.5) * 240,
      y: (Math.random() - 0.5) * 160 - 20,
      z: (Math.random() - 0.5) * 100,
      size: Math.random() * 4 + 3,
      color: Math.random() > 0.4 ? '#7A1C1C' : '#2D5BFF',
      phase: Math.random() * Math.PI * 2,
      speed: Math.random() * 0.03 + 0.01
    }))

    let time = 0

    const render = () => {
      time += 0.02
      rotX += (targetRotX + Math.sin(time * 0.8) * 0.05 - rotX) * 0.08
      rotY += (targetRotY + Math.cos(time * 0.6) * 0.08 + scrollOffset - rotY) * 0.08

      ctx.clearRect(0, 0, width, height)

      const cx = width / 2
      const cy = height / 2 + 15
      const fov = 350

      const project = (x, y, z) => {
        // Rotate Y
        const cosY = Math.cos(rotY)
        const sinY = Math.sin(rotY)
        const x1 = x * cosY - z * sinY
        const z1 = z * cosY + x * sinY

        // Rotate X
        const cosX = Math.cos(rotX - 0.25)
        const sinX = Math.sin(rotX - 0.25)
        const y2 = y * cosX - z1 * sinX
        const z2 = z1 * cosX + y * sinX

        const scale = fov / (fov + z2 + 200)
        return {
          x: cx + x1 * scale,
          y: cy + y2 * scale,
          scale,
          z: z2
        }
      }

      // Draw subtle circular pedestal / shadow
      ctx.save()
      ctx.beginPath()
      ctx.ellipse(cx, cy + 50, 90 * (1 + Math.sin(rotX) * 0.2), 30, 0, 0, Math.PI * 2)
      ctx.fillStyle = 'rgba(18, 24, 38, 0.06)'
      ctx.fill()
      ctx.restore()

      // Render Floating 3D Bars
      bars.forEach((b, idx) => {
        const floatY = Math.sin(time * 1.5 + idx * 0.7) * 4
        const barH = b.baseHeight + Math.sin(time * 2 + idx) * 3
        const halfW = b.width / 2
        const halfD = b.depth / 2
        const baseY = 40 + floatY
        const topY = baseY - barH

        // 8 vertices of cuboid
        const v = [
          project(b.x - halfW, topY, -halfD),  // 0: Top Front Left
          project(b.x + halfW, topY, -halfD),  // 1: Top Front Right
          project(b.x + halfW, baseY, -halfD), // 2: Bottom Front Right
          project(b.x - halfW, baseY, -halfD), // 3: Bottom Front Left
          project(b.x - halfW, topY, halfD),   // 4: Top Back Left
          project(b.x + halfW, topY, halfD),   // 5: Top Back Right
          project(b.x + halfW, baseY, halfD),  // 6: Bottom Back Right
          project(b.x - halfW, baseY, halfD)   // 7: Bottom Back Left
        ]

        // Front face
        ctx.beginPath()
        ctx.moveTo(v[0].x, v[0].y)
        ctx.lineTo(v[1].x, v[1].y)
        ctx.lineTo(v[2].x, v[2].y)
        ctx.lineTo(v[3].x, v[3].y)
        ctx.closePath()
        ctx.fillStyle = b.colorFront
        ctx.fill()
        ctx.strokeStyle = '#121826'
        ctx.lineWidth = 1.5
        ctx.stroke()

        // Top face
        ctx.beginPath()
        ctx.moveTo(v[4].x, v[4].y)
        ctx.lineTo(v[5].x, v[5].y)
        ctx.lineTo(v[1].x, v[1].y)
        ctx.lineTo(v[0].x, v[0].y)
        ctx.closePath()
        ctx.fillStyle = b.colorTop
        ctx.fill()
        ctx.stroke()

        // Right side face
        ctx.beginPath()
        ctx.moveTo(v[1].x, v[1].y)
        ctx.lineTo(v[5].x, v[5].y)
        ctx.lineTo(v[6].x, v[6].y)
        ctx.lineTo(v[2].x, v[2].y)
        ctx.closePath()
        ctx.fillStyle = b.colorSide
        ctx.fill()
        ctx.stroke()
      })

      // Render 3D Ascending Cyan Arrow above the 3rd bar
      const arrowBase = project(38, -85 + Math.sin(time * 2 + 2) * 5, 0)
      const arrowTip = project(50, -115 + Math.sin(time * 2 + 2) * 5, 0)
      const arrowLeft = project(28, -98 + Math.sin(time * 2 + 2) * 5, -10)
      const arrowRight = project(62, -98 + Math.sin(time * 2 + 2) * 5, 10)

      ctx.save()
      ctx.beginPath()
      ctx.moveTo(arrowTip.x, arrowTip.y)
      ctx.lineTo(arrowRight.x, arrowRight.y)
      ctx.lineTo(arrowBase.x, arrowBase.y)
      ctx.lineTo(arrowLeft.x, arrowLeft.y)
      ctx.closePath()
      ctx.fillStyle = '#22D3EE'
      ctx.fill()
      ctx.strokeStyle = '#121826'
      ctx.lineWidth = 2
      ctx.stroke()
      ctx.restore()

      // Render Sparkling Star Particles
      stars.forEach(s => {
        const floatStarY = s.y + Math.sin(time * 2 + s.phase) * 6
        const p = project(s.x, floatStarY, s.z)
        const size = s.size * p.scale * (1 + Math.sin(time * 3 + s.phase) * 0.3)

        ctx.save()
        ctx.translate(p.x, p.y)
        ctx.fillStyle = s.color

        // 4-point diamond star
        ctx.beginPath()
        ctx.moveTo(0, -size * 1.5)
        ctx.quadraticCurveTo(0, 0, size * 1.5, 0)
        ctx.quadraticCurveTo(0, 0, 0, size * 1.5)
        ctx.quadraticCurveTo(0, 0, -size * 1.5, 0)
        ctx.quadraticCurveTo(0, 0, 0, -size * 1.5)
        ctx.closePath()
        ctx.fill()
        ctx.strokeStyle = '#121826'
        ctx.lineWidth = 0.8
        ctx.stroke()
        ctx.restore()
      })

      const prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      if (!prefersReduced) {
        animationFrameId = requestAnimationFrame(render)
      }
    }

    render()

    return () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId)
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  return (
    <div className={`brand-3d-container ${className}`} style={{ position: 'relative', width: '100%', height: '100%', minHeight: 180, ...style }}>
      <canvas
        ref={canvasRef}
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          pointerEvents: 'none'
        }}
      />
    </div>
  )
}
